import { Injectable } from '@angular/core';
import { SortColumn, ValidDataType } from '../../views/report-details/interfaces/report-details.interface';
import { toCalendarDate } from '../helpers/calendar-date.utils';

@Injectable({
  providedIn: 'root'
})
export class SavedReportService {

  constructor() { }

  /**
   * Processes saved report filters to extract different filter types
   * @param tempReportFormData - The saved report form data
   * @param allColumnsData - All columns data for the report
   * @returns Object containing processed filter data
   */
  processSavedReportFilters(tempReportFormData: any, allColumnsData: any, primaryKeys:boolean = false): {
    savedReportFilters: any[];
    sortColumns: SortColumn[];
    dateFilterCol: string | number | null;
    periodValue: number | null;
    tempPreDefFilterIds: number[];
    recordsPerPage: number;
    custStartDate: string | number | any;
    custEndDate: string | number | any;
    } {
    const savedReportFilters: any[] = [];
    const sortColumns: SortColumn[] = [];
    let dateFilterCol: string | number | null = null;
    let periodValue: number | null = null;
    let tempPreDefFilterIds: number[] = [];
    let recordsPerPage: number = 10;
    let tempSortColObj: any = {};
    let custStartDate: string | number | any = null;
    let custEndDate: string | number | any = null;

    if (tempReportFormData.savedReportFilters?.length > 0) {
      tempReportFormData.savedReportFilters.forEach((filter: any) => {
        switch (filter.type) {
          case 4:
            if (filter.isCustomField == 1)
              filter.columnId = 'c' + filter.columnId;
            savedReportFilters.push(filter);
            break;
          case 5:
            let tempFilterColumnId = filter.columnId;
            if (filter.isCustomField == 1)
              tempFilterColumnId = 'c' + filter.columnId;
            const columnInfo = allColumnsData[tempFilterColumnId];
            if (columnInfo) {
              tempSortColObj = {
                code: tempFilterColumnId,
                name: columnInfo.columnName,
                sortOrder: filter.filterValue
              }
              if (primaryKeys) {
                tempSortColObj.id = filter.id;
              }
              sortColumns.push(tempSortColObj);
            }
            break;
          case 2:
            dateFilterCol = filter.columnId;
            custStartDate = toCalendarDate(filter.startDate);
            custEndDate = toCalendarDate(filter.endDate);
            periodValue = (filter.periodValue) ? parseInt(filter.periodValue) : 6;
            if (primaryKeys)
              dateFilterCol = filter;
            break;
          case 1:
            recordsPerPage = filter.recordsPerPage;
            if (primaryKeys)
              recordsPerPage = filter;
            break;
          case 3:
            tempPreDefFilterIds = filter.preDefFilterIds.split(',').map((id: string) => parseInt(id.trim()));
            if (primaryKeys)
              tempPreDefFilterIds = filter;
            break;
        }
      });
    }

    return { 
      savedReportFilters, 
      sortColumns, 
      dateFilterCol, 
      periodValue, 
      tempPreDefFilterIds, 
      recordsPerPage,
      custStartDate,
      custEndDate
    };
  }

  /**
   * Normalizes a dimension column id to the d-prefixed code used in form controls.
   */
  normalizeDimensionColumnCode(columnId: string | number): string {
    const idStr = String(columnId ?? '').trim();
    if (!idStr) {
      return idStr;
    }
    if (/^d/i.test(idStr)) {
      return 'd' + idStr.slice(1);
    }
    return 'd' + idStr;
  }

  /**
   * Normalizes dimension dropdown options so option codes consistently use the d-prefix.
   */
  normalizeDimensionColumnOptions(options: any[] = []): any[] {
    return (options || []).map((option: any) => ({
      ...option,
      code: this.normalizeDimensionColumnCode(option.code)
    }));
  }

  /**
   * Resolves the column code to use in the form, preferring a matching dimension option code.
   */
  resolveDimensionColumnCode(columnId: string | number, dimensionColumnsOptions: any[] = []): string {
    const normalizedCode = this.normalizeDimensionColumnCode(columnId);
    const rawId = String(columnId).replace(/^d/i, '');

    const matchingOption = (dimensionColumnsOptions || []).find((option: any) => {
      const optionCode = String(option.code);
      return optionCode === normalizedCode
        || optionCode === String(columnId)
        || optionCode === rawId
        || this.normalizeDimensionColumnCode(optionCode) === normalizedCode;
    });

    return matchingOption ? String(matchingOption.code) : normalizedCode;
  }

  private isDimensionValueColumn(tempReportFormData: any, col: any): boolean {
    return tempReportFormData.dimensionReport == 1 || col.isDimensionColumn == 1;
  }

  /**
   * Processes saved report columns to separate group by filters from regular columns
   */
  processSavedReportColumns(tempReportFormData: any, primaryKeys:boolean = false, allColumnsData: any = [], dimensionColumnsOptions: any = []): { columns: any; groupByFilters: any } {
    const normalizedDimensionOptions = this.normalizeDimensionColumnOptions(dimensionColumnsOptions);
    const columns: any = primaryKeys ? {} : [];
    const groupByFilters: any = primaryKeys ? {} : [];
    if (tempReportFormData.savedReportColumns?.length > 0) {
      tempReportFormData.savedReportColumns.forEach((col: any) => {
        if (col.forGroupBy == 1) {
          if (allColumnsData[col.columnId]) {
            if (primaryKeys) {
              groupByFilters[col.columnId] = col;
            } else {
              groupByFilters.push(col.columnId);
            }
          }
        } else if (this.isDimensionValueColumn(tempReportFormData, col)) {
          const columnCode = this.resolveDimensionColumnCode(col.columnId, normalizedDimensionOptions);
          const existsInOptions = normalizedDimensionOptions.some((option: any) =>
            String(option.code) === columnCode
          );

          if (col.isDimensionColumn == 1 || existsInOptions || tempReportFormData.dimensionReport == 1) {
            if (primaryKeys) {
              columns[columnCode] = col;
            } else {
              columns.push(columnCode);
            }
          }
        } else if (allColumnsData[col.columnId]) {
          if (primaryKeys) {
            columns[col.columnId] = col;
          } else {
            columns.push(col.columnId);
          }
        }
      });
    }
    return { columns, groupByFilters };
  }
} 